import { Color, ShaderMaterial as ShaderMaterialCtor } from 'three';
import type { ShaderMaterial } from 'three';
import type { MapDef } from '@cloudpearl/config';
import { ATMOSPHERE, BRUSH, LIGHTING, NOISE, SHADOW_FRAG_PARS, SHADOW_VERT_MAIN, SHADOW_VERT_PARS, TRAIL, WORLD_UNIFORMS } from '../shaders/chunks';
import type { SharedUniforms } from '../shaders/shared';
import { UNPACK, col, litMaterial } from './common';
import type { MaterialTextures } from './common';

/**
 * Terrain tiles: geometry gives the shape, a per-tile normal texture (1 m texels, computed from the
 * heightfield) gives smooth per-pixel shading — no triangle creases at any mesh resolution.
 * tNormal: R,G = normal.x,z (·0.5+0.5), B = ground mask (islands), A = cavity (valleys darker).
 */
const TILE_VS = /* glsl */ `
${SHADOW_VERT_PARS}
${WORLD_UNIFORMS}
attribute vec2 aTile;
uniform sampler2D tNormal;
varying vec3 vWorldPos;
varying vec2 vTile;
vec3 tileNormal(vec4 t) {
  vec2 nxz = t.rg * 2.0 - 1.0;
  return vec3(nxz.x, sqrt(max(1.0 - dot(nxz, nxz), 0.0)), nxz.y);
}
void main() {
  vec4 wPos = modelMatrix * vec4(position, 1.0);
  vec3 wNormal = tileNormal(texture2D(tNormal, aTile));
  vWorldPos = wPos.xyz;
  vTile = aTile;
  gl_Position = projectionMatrix * viewMatrix * wPos;
  ${SHADOW_VERT_MAIN}
}
`;

const TILE_FS_HEAD = /* glsl */ `
${SHADOW_FRAG_PARS}
${WORLD_UNIFORMS}
${NOISE}
${ATMOSPHERE}
${LIGHTING}
${BRUSH}
${UNPACK}
${TRAIL}
uniform sampler2D tNormal;
varying vec3 vWorldPos;
varying vec2 vTile;
vec3 tileNormal(vec4 t) {
  vec2 nxz = t.rg * 2.0 - 1.0;
  return vec3(nxz.x, sqrt(max(1.0 - dot(nxz, nxz), 0.0)), nxz.y);
}
`;

const MEADOW_FS = /* glsl */ `
${TILE_FS_HEAD}
uniform sampler2D tGround;
uniform vec3 uGroundA;
uniform vec3 uGroundB;
uniform vec3 uGroundC;
uniform vec3 uGroundDry;
uniform vec3 uCliff;
uniform vec3 uCliffDark;
uniform vec3 uGrassBase;
uniform vec3 uGrassTip;
uniform float uGrassFar;
uniform vec3 uFlowerColors[5];
uniform float uFlowerFar;
void main() {
  vec4 tn = texture2D(tNormal, vTile);
  if (tn.b < 0.5) discard;
  vec3 n0 = tileNormal(tn);
  vec2 p = vWorldPos.xz;
  vec3 dn = unpackN(texture2D(tGround, p * 0.22));
  vec3 n = normalize(n0 + vec3(dn.x, 0.0, dn.y) * 0.45);
  float dist = distance(vWorldPos, cameraPosition);

  // Painterly colour swaths (shared with the grass so blades sit on matching ground).
  vec4 br = texture2D(tBrush, p * 0.05);
  vec4 br2 = texture2D(tBrush, p * 0.013 + 0.37);
  float pt = meadowPatch(p);
  vec3 field = mix(uGroundA, uGroundB, smoothstep(0.26, 0.7, pt + (br2.r - 0.5) * 0.3));
  field = mix(field, uGroundC, smoothstep(0.62, 0.86, pt + (br.a - 0.5) * 0.35) * 0.45);
  field = mix(field, uGroundDry, smoothstep(0.7, 0.9, pt + (br.r - 0.5) * 0.25) * 0.55);
  // Far away the terrain *is* the meadow: blend toward the grass's lit mid-tone with a fine
  // blade-streak texture so it never reads as flat paint.
  vec3 grassMid = mix(uGrassBase, mix(uGrassTip, field, 0.5), 0.55);
  float streak = texture2D(tBrush, p * vec2(0.9, 0.35)).g;
  vec3 far = grassMid * (0.82 + streak * 0.3) * (0.9 + (br.b - 0.5) * 0.3);
  // Close up, under the blades, the ground is *turf*: a dense thatch of root-coloured grass with
  // fine streaks and a few dry flecks, matching the blades' lower halves — so any gap between
  // blades reads as deeper grass, never as bare ground.
  // (Skipped where the far tone has fully taken over — most of the screen.)
  float grassT = smoothstep(uGrassFar * 0.7, uGrassFar * 1.05, dist);
  vec3 albedo = far;
  if (grassT < 1.0) {
    vec2 tq = p * 2.6;
    float th = texture2D(tBrush, tq * vec2(1.0, 0.32)).g * 0.55 + texture2D(tBrush, tq.yx * vec2(0.85, 0.3) + 0.37).g * 0.45;
    float fleck = texture2D(tBrush, p * 7.3 + 0.11).b;
    vec3 rootTone = mix(uGrassBase * 0.85, mix(uGrassBase, uGrassTip, 0.4), smoothstep(0.25, 0.85, th));
    rootTone = mix(rootTone, mix(rootTone, uGroundDry * 0.7, 0.5), smoothstep(0.72, 0.92, fleck) * 0.35);
    vec3 soil = mix(rootTone, field * 0.6, 0.18) * (0.85 + texture2D(tGround, p * 0.22).a * 0.25);
    albedo = mix(soil, far, grassT);
  }
  // Beyond the drawn flowers, their drifts live on as soft dabs of colour in the meadow.
  float fd = smoothstep(uFlowerFar * 0.75, uFlowerFar * 1.1, dist);
  if (fd > 0.0) fd *= smoothstep(0.35, 0.8, flowerDrift(p));
  if (fd > 0.0) {
    int fsp = int(flowerSpecies(p) + 0.5);
    vec3 fc = uFlowerColors[0];
    if (fsp == 1) fc = uFlowerColors[1];
    else if (fsp == 2) fc = uFlowerColors[2];
    else if (fsp == 3) fc = uFlowerColors[3];
    else if (fsp == 4) fc = uFlowerColors[4];
    // Fine dots of petal colour (not a wash, which reads as bare earth from above).
    float speck = smoothstep(0.62, 0.8, texture2D(tBrush, p * 2.3 + 0.2).r);
    albedo = mix(albedo, fc * 1.15, fd * speck * 0.5);
  }
  // Gust waves continue across the far meadow where blades are too small to draw.
  if (dist > uGrassFar * 0.5) albedo *= 1.0 + gustWave(p) * 0.13 * smoothstep(uGrassFar * 0.5, uGrassFar, dist);
  // Where the pearl rolled, the turf is pressed flat: blades lying down catch more sky.
  vec2 tw = abs(p - uTrailWin.xy);
  if (uTrailInfo.z > 0.0 && max(tw.x, tw.y) < uTrailWin.z - 2.0) albedo = mix(albedo, grassMid * 1.05, trailAt(p).r * 0.65);
  // Exposed earth/rock on steep slopes.
  float steep = 1.0 - smoothstep(0.62, 0.86, n0.y + (br.r - 0.5) * 0.12);
  albedo = mix(albedo, mix(uCliff, uCliffDark, br.g * 0.7), steep);
  float ao = mix(0.72, 1.0, tn.a);
  float shadow = getShadowMask() * cloudShade(vWorldPos);
  vec3 c = paintLight(albedo, n, shadow, ao) * contactShade(vWorldPos);
  gl_FragColor = vec4(applyFog(c, vWorldPos), 1.0);
}
`;

const DUNE_FS = /* glsl */ `
${TILE_FS_HEAD}
uniform sampler2D tRippleShallow;
uniform sampler2D tRippleSteep;
uniform sampler2D tGrain;
uniform sampler2D tGlitter;
uniform vec3 uSandLit;
uniform vec3 uSandShade;
uniform vec3 uSandDeep;
uniform vec3 uSandSparkle;
uniform float uSparkle;
uniform float uRippleScale;

// After Alan Zucconi's "Journey Sand Shader" series (from John Edwards' GDC talk):
//   N = ripples(N); N = sandNormal(N);
//   colour = diffuseContrast + max(rim, oceanSpecular) + glitter
// tuned soft: gentle ripples, a calm diffuse ramp, sparing glitter.
void main() {
  vec4 tn = texture2D(tNormal, vTile);
  vec3 n0 = tileNormal(tn);
  vec3 wp = vWorldPos;
  vec3 toCam = cameraPosition - wp;
  float dist = length(toCam);
  vec3 V = toCam / dist;

  // Every detail term below fades out with distance; each is skipped (not just faded) where its
  // contribution is exactly zero. Neighbouring pixels take the same branch, so this is free.

  // --- The pearl's trail: a soft groove pressed into the sand (normal from the trail gradient).
  float groove = 0.0;
  vec2 tw = abs(wp.xz - uTrailWin.xy);
  if (uTrailInfo.z > 0.0 && max(tw.x, tw.y) < uTrailWin.z - 2.0) {
    groove = trailAt(wp.xz).r;
    float e = uTrailInfo.x;
    vec2 tg = vec2(trailAt(wp.xz + vec2(e, 0.0)).r - trailAt(wp.xz - vec2(e, 0.0)).r,
                   trailAt(wp.xz + vec2(0.0, e)).r - trailAt(wp.xz - vec2(0.0, e)).r) / (2.0 * e);
    n0 = normalize(n0 + vec3(tg.x, 0.0, tg.y) * 0.16);
  }

  // --- Ripples: shallow & steep normal maps oriented across the wind, blended by steepness.
  vec2 wd = normalize(uWind.xy);
  vec2 wperp = vec2(-wd.y, wd.x);
  vec2 ruv = vec2(dot(wp.xz, wd), dot(wp.xz, wperp)) / uRippleScale;
  vec3 rS = unpackN(texture2D(tRippleShallow, ruv));
  vec3 rT = unpackN(texture2D(tRippleSteep, ruv * 1.7 + 0.31));
  float steepness = pow(clamp(n0.y, 0.0, 1.0), 6.0);
  vec3 r = normalize(mix(rT, rS, steepness));
  // Slip faces avalanche smooth; ripples fade into tone with distance; the trail flattens them.
  float rStr = (1.0 - smoothstep(0.75, 0.55, n0.y) * 0.8) * (1.0 - smoothstep(40.0, 180.0, dist) * 0.85) * (1.0 - groove) * mix(0.6, 1.0, smoothstep(2.0, 14.0, dist));
  vec2 rw = r.x * wd + r.y * wperp;
  vec3 n = normalize(n0 + vec3(rw.x, 0.0, rw.y) * rStr * 0.36);

  // --- Sand normal: nlerp toward a random grain direction (fades out with distance).
  vec3 ng = n;
  if (dist < 20.0) {
    vec3 g = texture2D(tGrain, wp.xz * 1.4).xyz * 2.0 - 1.0;
    ng = normalize(mix(n, normalize(vec3(g.x, abs(g.z) + 0.6, g.y)), 0.12 * (1.0 - smoothstep(2.0, 20.0, dist))));
  }

  // --- Diffuse contrast (Journey): N.y squashed, a wide soft ramp between shade and lit sand.
  float shadow = getShadowMask() * cloudShade(vWorldPos);
  vec3 nj = normalize(ng * vec3(1.0, 0.45, 1.0));
  float ndl = dot(nj, uSunDir);
  float litAmt = smoothstep(-0.5, 1.0, ndl) * mix(0.35, 1.0, shadow);
  vec3 albedo = mix(uSandDeep, uSandShade, smoothstep(-0.8, 0.0, ndl));
  albedo = mix(albedo, uSandLit, litAmt);
  albedo *= 0.95 + (texture2D(tBrush, wp.xz * 0.006 + 0.3).r - 0.5) * 0.1 + (tn.a - 0.5) * 0.08;
  albedo *= 1.0 + (texture2D(tRippleShallow, ruv).a - 0.5) * 0.06 * rStr;
  // Pressed sand in the groove is a touch darker and smoother.
  albedo *= 1.0 - groove * 0.1;

  vec3 fill = hemiAmbient(ng) * mix(mix(uShadowTint / max(dot(uShadowTint, vec3(0.2126, 0.7152, 0.0722)), 1e-3), vec3(1.0), 0.8), vec3(1.0), litAmt);
  vec3 c = albedo * (fill + uSunColor * litAmt * 0.22);

  // --- Specular: rim light and "ocean" specular, combined with max() as in the talk.
  float rim = pow(1.0 - clamp(dot(n, V), 0.0, 1.0), 4.0) * 0.18;
  vec3 H = normalize(uSunDir + V);
  float ocean = pow(max(dot(n, H), 0.0), 60.0) * 0.07 * shadow;
  c += uSandLit * uSunColor * 0.35 * max(rim, ocean);

  // --- Glitter: a random facet per grain mirrors the sun now and then (stable as you move).
  if (dist > 3.0 && dist < 40.0) {
    vec4 gt = texture2D(tGlitter, wp.xz * 0.16);
    vec3 G = normalize(vec3(gt.x * 2.0 - 1.0, gt.z + 0.6, gt.y * 2.0 - 1.0));
    vec3 R = reflect(-uSunDir, normalize(mix(n, G, 0.8)));
    vec2 gcell = fract(wp.xz * 0.16 * 256.0) - 0.5;
    float dot2 = smoothstep(0.5, 0.15, length(gcell));
    float glint = pow(max(dot(R, V), 0.0), 600.0) * step(0.55, gt.a) * dot2;
    c += uSandSparkle * glint * smoothstep(3.0, 8.0, dist) * (1.0 - smoothstep(8.0, 40.0, dist)) * shadow * 9.0 * uSparkle * (1.0 - groove);
  }

  // --- Blowing sand: thin streams of saltating grains racing downwind over the surface, in gusty
  // waves, strongest on exposed crests and windward faces; out to mid distance.
  if (dist < 140.0 && uWind.z > 0.0) {
    vec2 sq = vec2(dot(wp.xz, wd), dot(wp.xz, wperp));
    float gust = vnoise(vec2(sq.x * 0.018 - uTime * 0.32, sq.y * 0.025)) * 0.7 + vnoise(vec2(sq.x * 0.05 - uTime * 0.7, sq.y * 0.06 + 9.0)) * 0.3;
    float exposure = clamp((tn.a - 0.45) * 3.0, 0.0, 1.0) * 0.5 + clamp(dot(n0.xz, -wd) * 4.0 + 0.6, 0.0, 1.0) * 0.5;
    float s1 = vnoise(vec2(sq.x * 0.32 - uTime * 6.5, sq.y * 3.4));
    float s2 = vnoise(vec2(sq.x * 0.85 - uTime * 10.0, sq.y * 8.0 + 4.0));
    float stream = smoothstep(0.52, 0.88, s1 * 0.6 + s2 * 0.4);
    float blow = stream * smoothstep(0.38, 0.72, gust) * exposure * uWind.z * (1.0 - smoothstep(25.0, 140.0, dist)) * (1.0 - groove * 0.5);
    c = mix(c, c * 1.1 + uSandLit * uSunColor * 0.05, clamp(blow, 0.0, 1.0) * 0.55);
  }

  c *= contactShade(wp);
  gl_FragColor = vec4(applyFog(c, wp), 1.0);
}
`;

export function createTerrainMaterial(shared: SharedUniforms, map: MapDef, tex: MaterialTextures, grassFar: number): ShaderMaterial {
  const p = map.palette;
  if (map.terrain.style === 'dune') {
    return litMaterial(
      shared,
      {
        tNormal: { value: null },
        tBrush: { value: tex.brush },
        tRippleShallow: { value: tex.rippleShallow },
        tRippleSteep: { value: tex.rippleSteep },
        tGrain: { value: tex.sandGrain },
        tGlitter: { value: tex.glitter },
        uSandLit: col(map.sand.lit),
        uSandShade: col(map.sand.shade),
        uSandDeep: col(map.sand.deep),
        uSandSparkle: col(map.sand.sparkle),
        uSparkle: { value: map.sand.sparkleDensity },
        uRippleScale: { value: map.sand.rippleScale },
      },
      { name: 'terrain-dune', vertexShader: TILE_VS, fragmentShader: DUNE_FS },
    );
  }
  return litMaterial(
    shared,
    {
      tNormal: { value: null },
      tBrush: { value: tex.brush },
      tGround: { value: tex.ground },
      uGroundA: col(p.groundA),
      uGroundB: col(p.groundB),
      uGroundC: col(p.groundC),
      uGroundDry: col(p.groundDry),
      uCliff: col(p.cliff),
      uCliffDark: col(p.cliffDark),
      uGrassBase: col(map.grass.base),
      uGrassTip: col(map.grass.tip),
      uGrassFar: { value: grassFar },
      uFlowerColors: { value: [0, 1, 2, 3, 4].map((i) => new Color(map.grass.flowers[i % Math.max(1, map.grass.flowers.length)] ?? '#ffffff').multiplyScalar(0.72)) },
      uFlowerFar: { value: map.grass.flowerRatio > 0 ? 46 : 1e6 },
    },
    { name: 'terrain-meadow', vertexShader: TILE_VS, fragmentShader: MEADOW_FS },
  );
}

/** Shadow-pass material for terrain tiles: cuts the coastline exactly like the colour pass. */
export function createTerrainDepthMaterial(): ShaderMaterial {
  return new ShaderMaterialCtor({
    name: 'terrain-depth',
    uniforms: { tNormal: { value: null } },
    vertexShader: /* glsl */ `
      attribute vec2 aTile;
      varying vec2 vTile;
      void main() {
        vTile = aTile;
        gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      uniform sampler2D tNormal;
      varying vec2 vTile;
      void main() {
        if (texture2D(tNormal, vTile).b < 0.5) discard;
        gl_FragColor = vec4(1.0);
      }
    `,
  });
}
